import './CancellationReportPage.css'
import { Row, Col } from 'react-bootstrap'
import { ICustomColumn, IDateSearchLocalStorageNames, IGridLocalStorageNames, IPageProps, IRootState } from 'types'
import { FormDateSpanSelector, PageHeader, ReturnButton } from 'components'
import { V1ApiCasesClient } from 'core/http-common'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import toast from 'core/toast'
import { Skeleton } from '@progress/kendo-react-indicators'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { Form, FormElement, FormHandle, FormRenderProps } from '@progress/kendo-react-form'
import { IFormDateSpanSelectorRef, RelativeDateOptionId } from 'components/Form/FormDateSpanSelector'
import moment from 'moment'
import { CaseCancellationReportDto, CaseCancellationReportValues, CaseViewEnum, DeletedStateEnum } from '@v1CasesApi'
import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle, ChartTooltip } from '@progress/kendo-react-charts'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'

const CancellationReportPage = ({ title }: IPageProps) => {
    const formDateSpanSelectorRef = useRef<IFormDateSpanSelectorRef>(null)
    const gridRef = useRef<ISAGridRef>(null)
    const formRef = useRef<FormHandle>(null)
    const [loaded, setLoaded] = useState(false)
    const defaultStartDate = moment().startOf('year').toDate()
    const defaultEndDate = moment().endOf('year').toDate()

    const [gridLocalStorageNames, setGridLocalStorageNames] = useState<IGridLocalStorageNames | undefined>({
        sort: 'cancellationReportSort',
        search: 'cancellationReportSearch',
        selectedPage: 'cancellationReportSelectedPage',
        pageSize: 'cancellationReportPageSize',
    })

    const dateSearchLocalStorageNames: IDateSearchLocalStorageNames = {
        relativeDateOption: 'cancellationReportRelativeDate',
        startDate: 'cancellationReportStartDate',
        endDate: 'cancellationReportEndDate',
    }


    const defaultValues = {
        startDate: defaultStartDate,
        endDate: defaultEndDate
    }

    const loading = useSelector((state: IRootState) => state.app.loading)
    const [cancellations, setCancellations] = useState<Array<CaseCancellationReportValues> | undefined>(undefined)
    const [report, setReport] = useState<CaseCancellationReportDto | undefined>(undefined)

    const searchByDate = async (start: Date, end: Date) => {
        formRef.current?.valueSetter('startDate', start)
        formRef.current?.valueSetter('endDate', end)
        gridRef.current?.fetch()
    }

    const DealerIdCell = (props: GridCellProps) => {
        return (
            <td className='cursor-pointer' title='Edit Dealer in a New Tab' onClick={(e) => e.stopPropagation()}>
                <a target='_blank' href={`${process.env.REACT_APP_OLD_SYS_ADMIN_URL}/web/Dealers/EditDealer.aspx?id=${props.dataItem.dealerId}`} >
                    {props.dataItem.dealerId}
                </a>
            </td>
        )
    }

    const ContractStatusCell = (props: GridCellProps) => {

        let status = ' '
        if (props.dataItem.contractEndDate) {
            const contractDate = moment(props.dataItem.contractEndDate)
            if (contractDate.isAfter(moment())) {
                status = (`In Contract: ${contractDate.format('MM/DD/YYYY')}`)
            } else {
                status = (`Expired: ${contractDate.format('MM/DD/YYYY')}`)
            }
        } else {
            status = ('No Contract Date')
        }

        return (
            <td className='text-left' >
                {status}
            </td>
        )
    }

    const cancellationReportColumns: ICustomColumn[] = [
        { field: 'createdDate', title: 'Created Date', format: '{0:d}', filterType: 'date', width: '145px' },
        { field: 'contractEndDate', title: ' Contract Status ', cell: ContractStatusCell, filterType: 'date', width: '165px' },
        { field: 'dealerId', title: 'DID', filterType: 'numeric', cell: DealerIdCell, width: '105px' },
        { field: 'dealerName', title: 'Account Name', filterType: 'text' },
        { field: 'createdByName', title: 'Created By', filterType: 'text', width: '195px' },
        { field: 'billingEffectiveDate', title: 'Billing Effective Date', format: '{0:d}', filterType: 'date', width: '165px' },
        { field: 'cancellationReasonName', title: 'Category', filterType: 'text', width: '345px' },
        { field: 'competitorName', title: 'Competitor', filterType: 'text', width: '125px' },
        { field: 'caseSubReason', title: 'Full or Partial Cancellation', filterType: 'text' },
    ]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleLegendItemClick = (event: any) => {
        event.preventDefault()
    }

    const fetchCases = async (props?: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            startLoadingAction()
            let values = formRef.current?.values
            let page = props?.paging && props.paging.currentPage ? props.paging.currentPage : 1

            const result = await V1ApiCasesClient.getCaseCancellationBySubReasonReport(
                DeletedStateEnum.Undefined,
                props?.search,
                props?.sortBy,
                props?.sortAscending,
                page,
                props?.paging ? props.paging.take : 25,
                true,
                values?.startDate ? values.startDate : defaultStartDate,
                values?.endDate ? values.endDate : defaultEndDate
            )
            setReport(result)
            setCancellations(result.totalsByType?.data ?? [])
            setLoaded(true)
            return {
                data: result?.data?.data ?? [],
                total: result?.data?.totalItems ?? result.data?.data.length ?? 0,


            }
        }

        finally {
            endLoadingAction()
        }
    }

    useEffect(() => {
        fetchCases()
    }, [])

    return (
        <div id='CancellationReportPage'>
            <PageHeader title={'Cancellation Requests Created'}>
                <ReturnButton redirectUrl='/reports' />
            </PageHeader>
            {gridLocalStorageNames && dateSearchLocalStorageNames &&
                <Form
                    ref={formRef}
                    ignoreModified={true}
                    initialValues={defaultValues}
                    render={(formRenderProps: FormRenderProps) => (
                        <FormElement>
                            <div className='bg-light border rounded p-3 shadow-sm mb-3'>
                                <Row>
                                    <Col lg={12}>
                                        <FormDateSpanSelector
                                            ref={formDateSpanSelectorRef}
                                            className=''
                                            formRenderProps={formRenderProps}
                                            localStorageNames={dateSearchLocalStorageNames}
                                            onSearch={searchByDate}
                                            disabled={loading}
                                            defaultRelativeDateOption={RelativeDateOptionId.Last90Days}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </FormElement>
                    )}
                />}
            <Row>
                <Col>
                    <div style={{ padding: '20px' }}>
                        {cancellations && report &&
                            <Chart onLegendItemClick={handleLegendItemClick}>
                                <ChartTitle text='Cancellation Requests Created' />
                                <ChartLegend position='bottom' orientation='horizontal' />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem
                                        categories={cancellations.map(item => item.cancellationTypeKey)}
                                    />
                                </ChartCategoryAxis>
                                <ChartSeries>
                                    <ChartSeriesItem
                                        type='column'
                                        data={cancellations.map(item => item.countValue)}
                                        name='Cancellation Count'
                                    />
                                </ChartSeries>
                                <ChartTooltip visible={true} />
                            </Chart>}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    {!loaded || !cancellations ? <Skeleton shape='rectangle' style={{ width: '100%', height: '500px' }} animation={{ type: 'pulse' }} />
                        :
                        <div style={{ padding: '20px' }}>
                            <Grid data={cancellations}>
                                <GridColumn
                                    field='cancellationTypeKey'
                                    title='Cancellation Type'
                                />
                                <GridColumn
                                    field='countValue'
                                    title='Count'
                                />
                            </Grid>
                        </div>
                    }
                </Col>
            </Row>

            <Row>
                <Col xs={12} className='my-3 cases-grid-page'>
                    <div className={'cancellation-report-grid'} style={{ padding: '20px' }}>

                        {!gridLocalStorageNames || !report ?
                            <Skeleton shape='rectangle' style={{ width: '100%', height: '500px' }} animation={{ type: 'pulse' }} />
                            :
                            <SAGrid
                                features={{
                                    exportToExcel: true,
                                    pageable: false,
                                    sortable: true,
                                    reorderColumns: true,
                                    title: 'Cancellation Requests Created',
                                    simpleSearch: true,
                                    resizable: true,
                                }}
                                columns={cancellationReportColumns}
                                ref={gridRef}
                                defaultSort={[{ field: 'createdDate', dir: 'desc' }]}

                                getData={fetchCases}
                                onRowClick={(e: { dataItem: { caseId: number } }) => window.open(`/cases/edit/${e.dataItem.caseId}`, '_blank')}
                                localStorageNames={gridLocalStorageNames}
                            />
                        }
                    </div>
                </Col>
            </Row>
        </div>
    )
}
export default CancellationReportPage
